import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../ui';
import { safeErrorMessage } from '../../utils/apiError';
import StudentPicker from './StudentPicker';
import styles from './Accounts.module.css';

// Choose which students a parent account is linked to. The selection
// replaces the parent's whole set of children.
function ChildrenModal({ token, parent, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!parent) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setError('');
    Promise.all([
      fetch('/api/v1/students', { headers }),
      fetch(`/api/v1/users/${parent.id}/children`, { headers }),
    ])
      .then(async ([studentsResponse, childrenResponse]) => {
        if (cancelled) return;
        if (!studentsResponse.ok || !childrenResponse.ok) {
          setError('Failed to load students');
          return;
        }
        setStudents(await studentsResponse.json());
        setSelectedIds((await childrenResponse.json()).map((child) => child.id));
      })
      .catch(() => { if (!cancelled) setError('Network error. Please check your connection and try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [parent, token]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`/api/v1/users/${parent.id}/children`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ student_ids: selectedIds }),
      });
      if (response.ok) {
        onSaved((await response.json()).length);
      } else {
        setError(await safeErrorMessage(response, 'Failed to update children'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={Boolean(parent)} onClose={onClose} title={parent ? `Children — ${parent.full_name}` : ''} width="520px">
      {parent && (
        <div>
          {error && <div className="alert alert-error">{error}</div>}
          <p className={styles.hint}>
            {parent.full_name} can see the profile, reports, attendance and fees of the students ticked here, and no one else's.
          </p>
          <StudentPicker students={students} selectedIds={selectedIds} onChange={setSelectedIds} loading={loading} />
          <div className={styles.actions}>
            <Button variant="primary" onClick={handleSave} disabled={saving || loading}>{saving ? 'Saving…' : 'Save children'}</Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default ChildrenModal;
